if (!window.customElements.get('rewards-purchase-timeline')) {
  class RewardsPurchaseTimeline extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.buildSection();

      this.timelineList = this.querySelector('[data-rewards-purchase-timeline-list]');
      this.timelineControls = this.querySelector('[data-rewards-purchase-timeline-controls]');
      this.loadMoreButton = this.querySelector('[data-rewards-purchase-timeline-load-more]');

      if (!this.timelineList || !this.timelineControls || !this.loadMoreButton) return;

      this.pageSize = 10;

      this.state = {
        isLoading: false,
        isLoadingAnotherPage: false,
        eventsLoaded: 0,
        rows: [],
        offset: 0,
        hasMore: true,
      };

      this.getTimeline();

      document.addEventListener('redeemPoints', () => this.getTimeline());
    }

    buildSection() {
      this.innerHTML = `
        <div class="${window.location.href.indexOf('/tools/recurring') >= 0 ? 'w-[calc(100%+40px)] -ml-5 px-5 lg:px-6 pt-5.25 pb-6 lg:py-6 bg-white lg:ml-0 lg:w-full lg:rounded-2xl' : 'block max-w-190 mt-8 mx-auto p-6 rounded-xl border border-base-neutral-300 bg-white'}">
          <h2 class="heading-md mb-2">
            ${window.ehs.strings.rewards.purchaseTimeline.heading}
          </h2>

          <p class="text-base mb-5">
            ${window.ehs.strings.rewards.purchaseTimeline.content}
          </p>

          <div data-rewards-purchase-timeline-list>
            <p class="text-base text-charcoal-green-700" data-rewards-purchase-timeline-loading>
              ${window.ehs.strings.rewards.purchaseTimeline.loadingTimeline}
            </p>
          </div>

          <div class="mt-5 justify-center hidden" data-rewards-purchase-timeline-controls>
            <button class="button button--tertiary" type="button" data-rewards-purchase-timeline-load-more hidden>
              ${window.ehs.strings.rewards.purchaseTimeline.loadMore}
            </button>
          </div>
        </div>
      `;
    }

    getTimeline() {
      this.waitForStore(30, 100)
        .then((store) => {
          return store.waitForReady(30, 100);
        })
        .then((store) => {
          this.loadMoreButton.addEventListener('click', () => {
            this.state.eventsLoaded = 0;
            this.loadPage(store, false, true);
          });

          return this.loadPage(store, true, true);
        })
        .catch(() => {
          this.renderEmpty();
        });
    }

    waitForStore(maxAttempts, delayMs) {
      return new Promise(function (resolve, reject) {
        let attempts = 0;

        function check() {
          const store = window.AntavoPointsStore;

          if (store && typeof store.waitForReady === 'function') {
            resolve(store);
            return;
          }

          attempts += 1;

          if (attempts >= maxAttempts) {
            reject(new Error('AntavoPointsStore unavailable'));
            return;
          }
          setTimeout(check, delayMs);
        }

        check();
      });
    }

    isNumberValue(value) {
      return value !== null && value !== undefined && !isNaN(Number(value));
    }

    titleize(raw) {
      return String(raw || '')
        .replace(/[_-]/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/\b\w/g, function (char) {
          return char.toUpperCase();
        });
    }

    getDescription(raw) {
      const descriptions = {
        recharge_credit_conversion: window.ehs.strings.rewards.purchaseTimeline.pointRedemption,
        referral_bonus: window.ehs.strings.rewards.purchaseTimeline.referralBonusPoints,
        checkout: window.ehs.strings.rewards.purchaseTimeline.checkout,
      };

      if (!raw) return 'Transaction';
      return descriptions[raw] || this.titleize(raw);
    }

    sortByDateDesc(items) {
      return items.slice().sort(function (a, b) {
        const aTime = a.dateObj.getTime();
        const bTime = b.dateObj.getTime();
        return bTime - aTime;
      });
    }

    formatDate(dateObj) {
      if (!dateObj) return '';

      return dateObj.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    }

    formatPoints(points) {
      if (!this.isNumberValue(points)) return { value: '-', className: 'is-neutral' };

      const numeric = Number(points);

      if (numeric > 0) {
        return {
          value: '+' + numeric.toLocaleString() + ' Pts',
          className: 'text-system-green-600',
        };
      }

      if (numeric < 0) {
        return {
          value: numeric.toLocaleString() + ' Pts',
          className: 'text-system-red-500',
        };
      }

      return { value: '-', className: 'text-charcoal-green-700' };
    }

    clearList() {
      while (this.timelineList.firstChild) {
        this.timelineList.removeChild(this.timelineList.firstChild);
      }
    }

    setButtonState() {
      if (this.state.rows.length === 0 || !this.state.hasMore) {
        this.loadMoreButton.hidden = true;
        this.timelineControls.classList.add('hidden');
        this.timelineControls.classList.remove('flex');
        return;
      }

      this.timelineControls.classList.remove('hidden');
      this.timelineControls.classList.add('flex');
      this.loadMoreButton.hidden = false;
      this.loadMoreButton.disabled = this.state.isLoadingAnotherPage;
      this.loadMoreButton.textContent = this.state.isLoadingAnotherPage
        ? window.ehs.strings.rewards.purchaseTimeline.loading
        : window.ehs.strings.rewards.purchaseTimeline.loadMore;
    }

    renderEmpty() {
      this.clearList();
      const empty = document.createElement('p');
      empty.className = 'text-base text-charcoal-green-700';
      empty.textContent = window.ehs.strings.rewards.purchaseTimeline.empty;
      this.timelineList.appendChild(empty);
      this.setButtonState();
    }

    renderRows(rows) {
      this.clearList();

      for (let i = 0; i < rows.length; i += 1) {
        const row = rows[i];
        const points = this.formatPoints(row.points);

        const itemEl = document.createElement('div');
        itemEl.classList.add(
          'flex',
          'items-center',
          'justify-between',
          'gap-4',
          'py-3.5',
          'border-b',
          'border-b-base-neutral-200',
          'last-of-type:border-transparent'
        );

        const leftEl = document.createElement('div');
        leftEl.className = 'min-w-0';

        const dateEl = document.createElement('p');
        dateEl.classList.add('m-0', 'text-sm', 'font-bold', 'text-charcoal-green-800');
        dateEl.textContent = this.formatDate(row.dateObj);

        const descEl = document.createElement('p');
        descEl.classList.add('mt-0.5', 'text-charcoal-green-700');
        descEl.textContent = this.getDescription(row.action);

        const pointsEl = document.createElement('p');
        pointsEl.classList.add('m-0', 'whitespace-nowrap', 'font-semibold', points.className);
        pointsEl.textContent = points.value;

        leftEl.appendChild(dateEl);
        leftEl.appendChild(descEl);
        itemEl.appendChild(leftEl);
        itemEl.appendChild(pointsEl);
        this.timelineList.appendChild(itemEl);
      }

      this.setButtonState();
    }

    normalizeRows(history) {
      const normalized = [];

      for (let i = 0; i < history.length; i += 1) {
        const raw = history[i];

        if (!raw || typeof raw !== 'object') continue;

        const row = {
          dateObj: new Date(raw.timestamp),
          descriptionKey: raw.id,
          descriptionLabel: raw.label,
          action: raw.action,
          points: raw.points,
        };

        normalized.push(row);
      }

      return this.sortByDateDesc(normalized);
    }

    renderLoading() {
      this.clearList();
      const loading = document.createElement('p');
      loading.className = 'text-base text-charcoal-green-700';
      loading.textContent = window.ehs.strings.rewards.purchaseTimeline.loadingTimeline;
      this.timelineList.appendChild(loading);
    }

    loadPage(store, reset, setButtonState) {
      if (this.state.isLoading) return Promise.resolve();

      this.state.isLoading = true;

      if (reset) {
        this.state.eventsLoaded = 0;
        this.state.rows = [];
        this.state.offset = 0;
        this.state.hasMore = true;
        this.renderLoading();
      }

      if (setButtonState) {
        this.setButtonState();
      }

      if (typeof store.fetchEvents !== 'function') {
        this.state.isLoading = false;
        return Promise.reject(new Error('AntavoPointsStore fetchEvents unavailable'));
      }

      return store
        .fetchEvents({
          limit: this.pageSize,
          offset: this.state.offset,
        })
        .then((payload) => {
          let filteredEvents = payload.data.filter((event) => event.action !== 'profile');

          if (window.ehs.rewards.transactionsOnly) {
            filteredEvents = filteredEvents.filter(
              (event) =>
                event.action.indexOf('point') >= 0 ||
                event.action.indexOf('credit_conversion') >= 0 ||
                event.action.indexOf('order') >= 0 ||
                event.action.indexOf('checkout') >= 0 ||
                event.action.indexOf('purchase') >= 0 ||
                event.action.indexOf('opt_in') >= 0 ||
                event.action.indexOf('tier') >= 0
            );
          }

          const pageRows = this.normalizeRows(filteredEvents);

          if (reset && pageRows.length === 0) {
            this.state.hasMore = false;
            this.renderEmpty();
            return;
          }

          this.state.eventsLoaded += filteredEvents.length;
          this.state.rows = this.state.rows.concat(pageRows);
          this.state.offset += this.pageSize;
          this.state.hasMore = payload.data.length === this.pageSize;

          if (this.state.rows.length === 0) {
            this.renderEmpty();
            return;
          }

          this.renderRows(this.state.rows);

          if (this.state.hasMore && this.state.eventsLoaded < this.pageSize) {
            this.state.isLoadingAnotherPage = true;

            setTimeout(() => {
              this.loadPage(store, false, false);
            }, 100);
          } else {
            this.state.isLoadingAnotherPage = false;
          }
        })
        .catch(() => {
          if (this.state.rows.length === 0) {
            this.renderEmpty();
          } else {
            this.renderRows(this.state.rows);
          }
        })
        .finally(() => {
          this.state.isLoading = false;
          this.setButtonState();
        });
    }
  }

  window.customElements.define('rewards-purchase-timeline', RewardsPurchaseTimeline);
}
